import { Check } from "lucide-react"
import { useState, useEffect } from "react"

const themes = [
  { name: "Blue", value: "blue", color: "hsl(221.2, 83.2%, 53.3%)" },
  { name: "Green", value: "green", color: "hsl(142.1, 76.2%, 36.3%)" },
  { name: "Neutral", value: "neutral", color: "hsl(215, 16.3%, 46.9%)" },
  { name: "Orange", value: "orange", color: "hsl(27.8, 87.2%, 57.8%)" },
  { name: "Red", value: "red", color: "hsl(0, 84.2%, 60.2%)" },
  { name: "Rose", value: "rose", color: "hsl(346.8, 77.2%, 49.8%)" },
  { name: "Violet", value: "violet", color: "hsl(262.1, 83.3%, 57.8%)" },
  { name: "Yellow", value: "yellow", color: "hsl(47.9, 95.8%, 53.1%)" },
]

export function ThemeColorSelector() {
  const [currentTheme, setCurrentTheme] = useState("blue")

  useEffect(() => {
    const saved = localStorage.getItem("theme-color") || "blue"
    setCurrentTheme(saved)
    applyTheme(saved)

    // Ouvir mudanças no tema light/dark para reaplicar cores
    const handleThemeColorChange = (e: CustomEvent) => {
      applyTheme(e.detail)
    }

    window.addEventListener("theme-color-change", handleThemeColorChange as EventListener)
    
    return () => {
      window.removeEventListener("theme-color-change", handleThemeColorChange as EventListener)
    }
  }, [])

  const applyTheme = (theme: string) => {
    // Don't override a non-custom preset's colors
    const activePreset = document.documentElement.getAttribute("data-preset") || "custom"
    if (activePreset !== "custom") return

    const root = document.documentElement
    const isDark = root.classList.contains('dark')
    
    // Remover todas as classes de tema anteriores
    themes.forEach(t => root.classList.remove(`theme-${t.value}`))
    
    // Adicionar nova classe de tema
    root.classList.add(`theme-${theme}`)
    
    // Aplicar cores baseadas no tema selecionado
    switch (theme) {
      case "blue":
        root.style.setProperty("--primary", isDark ? "217.2 91.2% 59.8%" : "221.2 83.2% 53.3%")
        root.style.setProperty("--primary-foreground", isDark ? "222.2 47.4% 11.2%" : "210 40% 98%")
        root.style.setProperty("--ring", isDark ? "224.3 76.3% 48%" : "221.2 83.2% 53.3%")
        root.style.setProperty("--chart-1", isDark ? "217.2 91.2% 59.8%" : "221.2 83.2% 53.3%")
        root.style.setProperty("--sidebar-primary", isDark ? "217.2 91.2% 59.8%" : "221.2 83.2% 53.3%")
        root.style.setProperty("--sidebar-ring", isDark ? "217.2 91.2% 59.8%" : "221.2 83.2% 53.3%")
        break
      case "green":
        root.style.setProperty("--primary", isDark ? "142.1 70.6% 45.3%" : "142.1 76.2% 36.3%")
        root.style.setProperty("--primary-foreground", "210 40% 98%")
        root.style.setProperty("--ring", isDark ? "142.1 70.6% 45.3%" : "142.1 76.2% 36.3%")
        root.style.setProperty("--chart-1", isDark ? "142.1 70.6% 45.3%" : "142.1 76.2% 36.3%")
        root.style.setProperty("--sidebar-primary", isDark ? "142.1 70.6% 45.3%" : "142.1 76.2% 36.3%")
        root.style.setProperty("--sidebar-ring", isDark ? "142.1 70.6% 45.3%" : "142.1 76.2% 36.3%")
        break
      case "neutral":
        root.style.setProperty("--primary", isDark ? "215 20.2% 65.1%" : "215 16.3% 46.9%")
        root.style.setProperty("--primary-foreground", isDark ? "222.2 47.4% 11.2%" : "210 40% 98%")
        root.style.setProperty("--ring", isDark ? "215 20.2% 65.1%" : "215 16.3% 46.9%")
        root.style.setProperty("--chart-1", isDark ? "215 20.2% 65.1%" : "215 16.3% 46.9%")
        root.style.setProperty("--sidebar-primary", isDark ? "215 20.2% 65.1%" : "215 16.3% 46.9%")
        root.style.setProperty("--sidebar-ring", isDark ? "215 20.2% 65.1%" : "215 16.3% 46.9%")
        break
      case "orange":
        root.style.setProperty("--primary", "27.8 87.2% 57.8%")
        root.style.setProperty("--primary-foreground", "210 40% 98%")
        root.style.setProperty("--ring", "27.8 87.2% 57.8%")
        root.style.setProperty("--chart-1", "27.8 87.2% 57.8%")
        root.style.setProperty("--sidebar-primary", "27.8 87.2% 57.8%")
        root.style.setProperty("--sidebar-ring", "27.8 87.2% 57.8%")
        break
      case "red":
        root.style.setProperty("--primary", "0 84.2% 60.2%")
        root.style.setProperty("--primary-foreground", "210 40% 98%")
        root.style.setProperty("--ring", "0 84.2% 60.2%")
        root.style.setProperty("--chart-1", "0 84.2% 60.2%")
        root.style.setProperty("--sidebar-primary", "0 84.2% 60.2%")
        root.style.setProperty("--sidebar-ring", "0 84.2% 60.2%")
        break
      case "rose":
        root.style.setProperty("--primary", "346.8 77.2% 49.8%")
        root.style.setProperty("--primary-foreground", "210 40% 98%")
        root.style.setProperty("--ring", "346.8 77.2% 49.8%")
        root.style.setProperty("--chart-1", "346.8 77.2% 49.8%")
        root.style.setProperty("--sidebar-primary", "346.8 77.2% 49.8%")
        root.style.setProperty("--sidebar-ring", "346.8 77.2% 49.8%")
        break
      case "violet":
        root.style.setProperty("--primary", "262.1 83.3% 57.8%")
        root.style.setProperty("--primary-foreground", "210 40% 98%")
        root.style.setProperty("--ring", "262.1 83.3% 57.8%")
        root.style.setProperty("--chart-1", "262.1 83.3% 57.8%")
        root.style.setProperty("--sidebar-primary", "262.1 83.3% 57.8%")
        root.style.setProperty("--sidebar-ring", "262.1 83.3% 57.8%")
        break
      case "yellow":
        root.style.setProperty("--primary", "47.9 95.8% 53.1%")
        root.style.setProperty("--primary-foreground", isDark ? "222.2 47.4% 11.2%" : "210 40% 98%")
        root.style.setProperty("--ring", "47.9 95.8% 53.1%")
        root.style.setProperty("--chart-1", "47.9 95.8% 53.1%")
        root.style.setProperty("--sidebar-primary", "47.9 95.8% 53.1%")
        root.style.setProperty("--sidebar-ring", "47.9 95.8% 53.1%")
        break
    }
  }

  const handleThemeChange = (theme: string) => {
    setCurrentTheme(theme)
    localStorage.setItem("theme-color", theme)
    applyTheme(theme)
  }

  return (
    <div className="flex flex-wrap gap-2 justify-center">
      {themes.map((theme) => (
        <button
          key={theme.value}
          onClick={() => handleThemeChange(theme.value)}
          className={`relative w-10 h-10 rounded-full border-3 transition-all hover:scale-110 ${
            currentTheme === theme.value
              ? 'border-primary shadow-lg ring-2 ring-primary/20'
              : 'border-border hover:border-primary/50'
          }`}
          style={{ backgroundColor: theme.color }}
          title={theme.name}
        >
          {currentTheme === theme.value && (
            <div className="absolute inset-0 rounded-full bg-black/20 flex items-center justify-center">
              <Check className="h-4 w-4 text-white drop-shadow-sm" />
            </div>
          )}
        </button>
      ))}
    </div>
  )
}
